import React from 'react';
import { PieChart, DollarSign, Percent } from 'lucide-react';

const HoldingsWidget = ({ holdings }) => {
    // Default fallback data if no holdings are provided
    const defaultHoldings = [
        { asset_type: "Equity", total_investment_value: 1694590, allocation_percentage: "59.56" },
        { asset_type: "Fixed Income", total_investment_value: 853500, allocation_percentage: "30.00" },
        { asset_type: "Real Estate", total_investment_value: 109660, allocation_percentage: "3.85" },
        { asset_type: "Commodities", total_investment_value: 73450, allocation_percentage: "2.58" },
        { asset_type: "Cash & Equivalents", total_investment_value: 56900, allocation_percentage: "2.00" },
        { asset_type: "Mutual Fund", total_investment_value: 28450, allocation_percentage: "1.00" },
        { asset_type: "Private Equity", total_investment_value: 28450, allocation_percentage: "1.00" }
    ];

    const displayHoldings = holdings || defaultHoldings;

    // Format currency values
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Get color based on asset type - using more professional, muted colors
    const getAssetColor = (assetType) => {
        const colorMap = {
            'Equity': 'bg-blue-500/70',
            'Fixed Income': 'bg-emerald-500/70',
            'Real Estate': 'bg-amber-500/70',
            'Commodities': 'bg-rose-500/70',
            'Cash & Equivalents': 'bg-violet-500/70',
            'Mutual Fund': 'bg-indigo-500/70',
            'Private Equity': 'bg-fuchsia-500/70',
            'Other': 'bg-slate-500/70'
        };
        
        return colorMap[assetType] || 'bg-slate-500/70';
    };

    // Get border color for asset type
    const getAssetBorderColor = (assetType) => {
        const colorMap = {
            'Equity': 'border-blue-400/30',
            'Fixed Income': 'border-emerald-400/30',
            'Real Estate': 'border-amber-400/30',
            'Commodities': 'border-rose-400/30',
            'Cash & Equivalents': 'border-violet-400/30',
            'Mutual Fund': 'border-indigo-400/30',
            'Private Equity': 'border-fuchsia-400/30',
            'Other': 'border-slate-400/30'
        };
        
        return colorMap[assetType] || 'border-slate-400/30';
    };

    // Calculate total portfolio value
    const totalPortfolioValue = displayHoldings.reduce(
        (sum, holding) => sum + holding.total_investment_value, 0
    );

    return (
        <div
            className="p-5 bg-slate-800/40 backdrop-blur-sm rounded-lg relative overflow-hidden h-full border border-slate-700/50"
        >
            {/* Subtle decorative gradient */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-500/20 to-transparent"></div>
           
            <div className="flex justify-between items-center mb-5 relative z-10">
                <h2 className="text-lg font-semibold text-slate-100">Asset Allocation</h2>
                <div className="text-xs text-slate-400 font-medium">
                    Total: <span className="text-slate-300">{formatCurrency(totalPortfolioValue)}</span>
                </div>
            </div>

            <div className="overflow-x-auto relative z-10">
                <table className="min-w-full">
                    {/* <thead>
                        <tr className="text-left text-xs font-medium text-slate-400 border-b border-slate-700/50">
                            <th className="pb-3 font-medium">Asset Type</th>
                            <th className="pb-3 text-right font-medium">Value</th>
                            <th className="pb-3 text-right font-medium">Allocation</th>
                        </tr>
                    </thead> */}
                    <tbody className="divide-y divide-slate-700/30">
                        {displayHoldings.map((holding, index) => (
                            <tr key={index} className="group hover:bg-slate-700/10 transition-colors">
                                <td className="py-3">
                                    <div className="flex items-center gap-2.5">
                                        <div className={`w-2 h-2 rounded-full ${getAssetColor(holding.asset_type)} ring-1 ${getAssetBorderColor(holding.asset_type)}`}></div>
                                        <span className="text-sm font-medium text-slate-200">{holding.asset_type}</span>
                                    </div>
                                </td>
                                <td className="py-3 text-right">
                                    <span className="text-sm text-slate-300 font-medium tabular-nums">
                                        {formatCurrency(holding.total_investment_value)}
                                    </span>
                                </td>
                                <td className="py-3 text-right">
                                    <span className="text-sm text-slate-300 font-medium tabular-nums">
                                        {parseFloat(holding.allocation_percentage).toFixed(2)}%
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {/* Visual representation of allocation with muted colors */}
            <div className="mt-5 h-1.5 bg-slate-700/30 rounded-full overflow-hidden relative z-10 shadow-inner">
                {displayHoldings.map((holding, index) => {
                    const width = `${parseFloat(holding.allocation_percentage)}%`;
                    const left = `${displayHoldings
                        .slice(0, index)
                        .reduce((sum, h) => sum + parseFloat(h.allocation_percentage), 0)}%`;
                    
                    return (
                        <div
                            key={index}
                            className={`absolute top-0 bottom-0 ${getAssetColor(holding.asset_type)} transition-all`}
                            style={{ width, left }}
                        ></div>
                    );
                })}
            </div>
        </div>
    );
};

export default HoldingsWidget;